import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authApi, userCache } from "./api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => userCache.get());
  const [loading, setLoading] = useState(true);

  // Hydrate session from the HTTP-only cookie by calling /auth/me
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authApi.me();
        if (!cancelled) {
          setUser(res.data);
          userCache.set(res.data);
        }
      } catch (err) {
        if (!cancelled) {
          setUser(null);
          userCache.clear();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await authApi.login(email, password);
    const nextUser = res.data?.user || null;
    setUser(nextUser);
    userCache.set(nextUser);
    return nextUser;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (err) {
      console.warn("Logout request failed", err);
    }
    setUser(null);
    userCache.clear();
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout }),
    [user, loading, login, logout]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => useContext(AuthCtx);
